import { Stack } from "@mui/material";
import { Typography } from "@platform/design-system";

import { SOURCE_LABELS } from "../labels";

import type { CensusValidation } from "../hooks/useCensusValidation";

interface CensusPageHeadingProps {
  readonly validation: CensusValidation;
  readonly clientName?: string;
}

const readDate = (readAt?: string): string | undefined => {
  if (!readAt) {
    return undefined;
  }

  const parsed = new Date(readAt);

  return Number.isNaN(parsed.getTime())
    ? undefined
    : parsed.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

/**
 * Where this census came from, in one line.
 *
 * Each part is dropped when unknown rather than shown empty, so an RFP whose
 * census has never been read carries a heading and nothing misleading under
 * it.
 */
const subtitleParts = (
  validation: CensusValidation,
  clientName?: string,
): string[] => {
  const { source, employees } = validation;

  // A read that produced nobody leaves the count unknown, not zero. Saying
  // "0 eligible employees" beside a census that failed to parse states as
  // fact something we never learned.
  const countKnown = validation.members > 0;

  return [
    clientName ? `Census provided by ${clientName}` : undefined,
    source ? (SOURCE_LABELS[source.source] ?? source.source) : undefined,
    countKnown ? `${employees} eligible employees` : undefined,
    readDate(source?.readAt),
  ].filter((part): part is string => part !== undefined);
};

export const CensusPageHeading = ({
  validation,
  clientName,
}: CensusPageHeadingProps): React.JSX.Element => {
  const parts = subtitleParts(validation, clientName);

  return (
    <Stack gap={0.5} data-testid="census-page-heading">
      {/* display-small maps to h3, the variant the tab heading used. */}
      <Typography variant="display-small">Census Validation</Typography>

      {parts.length > 0 && (
        <Typography variant="body-medium" color="textSecondary">
          {parts.join(" · ")}
        </Typography>
      )}
    </Stack>
  );
};

export { subtitleParts };
